import { useEffect, useRef, useState } from 'react';
import { RotateCw, X, Check } from 'lucide-react';
import { Button } from '@/components/ui';

// Lightweight square crop/rotate editor (no library). The image is shown in a
// square frame; the user zooms, pans, and rotates, then we render the framed
// region to a canvas and return a JPEG data URL. Used to re-frame photos that
// came in too tightly cropped (or sideways) from a phone.

const FRAME = 300; // px, the square editing viewport
const QUALITY = 0.92;
const OUT_SIZE = 1600; // exported square resolution

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

// Rotate a data URL by 90° steps, returning a new data URL.
async function rotated(src: string, deg: number): Promise<string> {
  const img = await loadImage(src);
  const canvas = document.createElement('canvas');
  const swap = deg % 180 !== 0;
  canvas.width = swap ? img.naturalHeight : img.naturalWidth;
  canvas.height = swap ? img.naturalWidth : img.naturalHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) return src;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((deg * Math.PI) / 180);
  ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
  return canvas.toDataURL('image/jpeg', QUALITY);
}

export function PhotoCropper({
  image,
  onCancel,
  onApply,
}: {
  image: string;
  onCancel: () => void;
  onApply: (dataUrl: string) => void;
}) {
  const [src, setSrc] = useState(image);
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [busy, setBusy] = useState(false);
  const drag = useRef<{ x: number; y: number; px: number; py: number } | null>(null);

  useEffect(() => {
    let alive = true;
    loadImage(src).then((i) => {
      if (alive) {
        setImg(i);
        setPan({ x: 0, y: 0 });
        setZoom(1);
      }
    });
    return () => {
      alive = false;
    };
  }, [src]);

  // At zoom 1 the image covers the square (object-cover).
  const baseScale = img ? FRAME / Math.min(img.naturalWidth, img.naturalHeight) : 1;
  const dispW = img ? img.naturalWidth * baseScale * zoom : FRAME;
  const dispH = img ? img.naturalHeight * baseScale * zoom : FRAME;

  const onPointerDown = (e: React.PointerEvent) => {
    drag.current = { x: e.clientX, y: e.clientY, px: pan.x, py: pan.y };
    (e.target as Element).setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag.current) return;
    setPan({ x: drag.current.px + (e.clientX - drag.current.x), y: drag.current.py + (e.clientY - drag.current.y) });
  };
  const onPointerUp = () => {
    drag.current = null;
  };

  const handleRotate = async () => {
    setBusy(true);
    try {
      setSrc(await rotated(src, 90));
    } finally {
      setBusy(false);
    }
  };

  const handleApply = () => {
    if (!img) return;
    const scale = baseScale * zoom;
    const imgLeft = (FRAME - dispW) / 2 + pan.x;
    const imgTop = (FRAME - dispH) / 2 + pan.y;
    // Source rectangle of the image visible under the square frame.
    const sx = -imgLeft / scale;
    const sy = -imgTop / scale;
    const sSize = FRAME / scale;
    const out = OUT_SIZE;
    const canvas = document.createElement('canvas');
    canvas.width = out;
    canvas.height = out;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, out, out);
    ctx.drawImage(img, sx, sy, sSize, sSize, 0, 0, out, out);
    onApply(canvas.toDataURL('image/jpeg', QUALITY));
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-4">
      <div className="w-full max-w-sm bg-background rounded-3xl border border-border overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h2 className="text-sm font-bold text-foreground">Adjust photo</h2>
          <button onClick={onCancel} className="p-1.5 rounded-full hover:bg-secondary">
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        <div className="p-4 flex flex-col items-center gap-4">
          <div
            className="relative overflow-hidden rounded-xl bg-secondary touch-none select-none"
            style={{ width: FRAME, height: FRAME, maxWidth: '100%' }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            {img && (
              <img
                src={src}
                alt=""
                draggable={false}
                className="absolute pointer-events-none max-w-none"
                style={{
                  width: dispW,
                  height: dispH,
                  left: (FRAME - dispW) / 2 + pan.x,
                  top: (FRAME - dispH) / 2 + pan.y,
                }}
              />
            )}
            <div className="absolute inset-0 ring-1 ring-inset ring-white/40 rounded-xl pointer-events-none" />
          </div>

          <div className="w-full flex items-center gap-3">
            <span className="text-xs text-muted-foreground">Zoom</span>
            <input
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1 accent-primary"
            />
            <button
              type="button"
              onClick={handleRotate}
              disabled={busy}
              className="p-2 rounded-lg bg-secondary text-foreground hover:bg-muted transition-colors"
              aria-label="Rotate"
            >
              <RotateCw className="w-4 h-4" />
            </button>
          </div>
          <p className="text-[10px] text-muted-foreground -mt-2 self-start">
            Drag to reposition · pinch/zoom with the slider
          </p>
        </div>

        <div className="px-4 py-3 border-t border-border flex gap-2">
          <Button variant="outline" fullWidth onClick={onCancel}>
            Cancel
          </Button>
          <Button fullWidth onClick={handleApply} disabled={!img}>
            <Check className="w-4 h-4" />
            Apply
          </Button>
        </div>
      </div>
    </div>
  );
}
